import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import UserAccount from './nps-components/landing/account/UserAccount';
import VerticalNavbar from './nps-components/landing/navbar/VerticalNavbar';
import { useAuth } from "./contexts/AuthContext";
import GlobalLoader from './nps-components/common/GlobalLoader';
import GlobalNotification from './nps-components/common/GlobalNotification';
import Footer from './nps-components/common/Footer';
import { Route, Routes } from "react-router-dom";
import TaskBoard from './nps-components/task/TaskBoard';
import TaskDetail from './nps-components/task/TaskDetail';
import WebNotificationDetail from './nps-components/common/WebNotificationDetail';
import WebNotificationList from './nps-components/common/WebNotificationList';
import { WebNotificationProvider } from "./contexts/WebNotificationContext";

const ProtectedRoute = () => {
  const { user } = useAuth(); // Check if user is logged in
  return user ? <Outlet /> : <Navigate to="/SignIn" replace />;
};

const App = () => {
  const { user } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate(); // Add this hook

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = (event) => {
        
        if (event.data.type === 'NOTIFICATION_CLICK') {
          const targetUrl = event.data.url;
          navigate(targetUrl);
        }
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);

      // Cleanup
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, [navigate]);

  return (
    <WebNotificationProvider userId={user?.id}>
      <div className="flex flex-col h-screen">
        <GlobalLoader />
        <GlobalNotification />

        {/* mobile header (always visible; Desktop will still show content layout) */}
        <header className="md:hidden bg-white border-b">
          <div className="max-w-screen-xl mx-auto px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                aria-label="Open menu"
                onClick={() => setMobileNavOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                {/* simple hamburger */}
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="text-lg font-semibold">Task Portal</div>
            </div>

            {/* ensure user/account is visible on mobile */}
            <div className="flex items-center">
              <UserAccount />
            </div>
          </div>
        </header>

        <div className="flex flex-1">
          {/* desktop sidebar */}
          <aside className="hidden md:block">
            <VerticalNavbar />
          </aside>

          {/* mobile overlay sidebar */}
          {mobileNavOpen && (
            <div className="fixed inset-0 z-50 md:hidden">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setMobileNavOpen(false)}
                aria-hidden="true"
              />
              <div className="relative z-50 w-64 h-full bg-white shadow-lg">
                <div className="p-3 border-b flex items-center justify-between">
                  <div className="font-medium">Menu</div>
                  <button
                    aria-label="Close menu"
                    onClick={() => setMobileNavOpen(false)}
                    className="p-2 rounded-md hover:bg-gray-100"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="h-full overflow-y-auto">
                  {/* allow VerticalNavbar to render inside App overlay — disable its internal mobile header/drawer */}
                  <VerticalNavbar disableInternalMobile={true} onNavigate={() => setMobileNavOpen(false)} />

                </div>
              </div>
            </div>
          )}

          <main className="flex flex-col flex-1 bg-gray-100">
            {/* desktop top area: show account at top on md+ as well */}
            <div className="hidden md:block bg-white border-b">
              <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between">
                <div className="text-xl font-semibold">Task Portal</div>
                <div className="flex items-center">
                  <UserAccount />
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto py-4 px-2">
              <Routes>
                <Route path="home" element={<TaskBoard />} />
                <Route path="notifications" element={<WebNotificationList userId={user?.id} />} />
                <Route path="notifications/:id" element={<WebNotificationDetail userId={user?.id} />} />
                <Route path="tasks/:taskId" element={<TaskDetail />} />

              </Routes>
            </div>
          </main>
        </div>

        <Footer />
      </div>
    </WebNotificationProvider>
  );
}

export { App, ProtectedRoute };
